import React from 'react';
import {
  Hero,
  PartnersSection,
  ProblemSection,
  ServicesSection,
  TestimonialsSection,
  UrgencySection,
  GuaranteeSection,
} from '../components/landing';

export const HomePage: React.FC = () => {
  return (
    <main>
      {/* Hero Section */}
      <Hero
        eyebrow="AI Implementation for Mid-Market Companies"
        headline="Cut Operating Costs 30-50% With Production AI — Deployed in Weeks"
        subheadline="We build and deploy AI systems that actually ship. 127+ production deployments. $12M+ in documented savings. No strategy decks. No science projects. Just results."
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
        metrics={[
          { value: '127+', label: 'AI Systems Deployed' },
          { value: '$12M+', label: 'Client Savings' },
          { value: '23', label: 'Days Avg. Deploy' },
        ]}
      />

      {/* Partners Section */}
      <PartnersSection />

      {/* Problem Section */}
      <ProblemSection
        headline="The $847,000 Problem You Don't Know You Have"
        intro="Right now, somewhere in your company, there's a team of 4-6 people doing work that AI could handle in minutes. You already know this. You've seen the demos. You've read the case studies. And yet... nothing has shipped."
        painPoints={[
          {
            title: 'Your best engineers are drowning in manual processes',
            description:
              "They're spending 60% of their time on tasks that should be automated — data entry, report generation, ticket routing, document processing. Every week, you're paying senior salaries for junior work.",
          },
          {
            title: "Your competitors are shipping AI while you're still 'evaluating'",
            description:
              "That startup that launched 18 months after you? They just announced their AI-powered feature. Meanwhile, your AI initiative is on slide 47 of a strategy deck that nobody's read since Q2.",
          },
          {
            title: "You've already burned budget on AI projects that went nowhere",
            description:
              "Maybe it was the chatbot that couldn't handle real questions. Maybe it was the ML model that worked in the demo but failed in production. Now there's scar tissue. The team is skeptical. Leadership is hesitant.",
          },
          {
            title: 'Every month you wait costs you $70,000+',
            description:
              "Not in 'opportunity cost' — in actual dollars. The salaries for manual work. The deals lost to faster competitors. The customer churn from slow response times.",
          },
        ]}
        fascinationBullet="The uncomfortable truth about why 73% of AI initiatives never make it to production (and the 2-question diagnostic that predicts which side you'll land on)"
        transition="That's exactly why we built a different approach."
      />

      {/* Services Section */}
      <ServicesSection
        headline="What We Build — And What It Saves You"
        subtitle="Three services. Most clients start with the Audit, then build the systems that surfaced the biggest ROI."
        services={[
          {
            title: 'AI Audit Partnership',
            problem:
              "You don't know which AI opportunities are real ROI and which are expensive distractions — and even if you did, you don't have a team that can ship them.",
            solution:
              'An ongoing audit + build partnership. We systematically map every process, prioritize the highest-ROI AI opportunities, and ship the systems that capture them — quarter after quarter.',
            outcome:
              'Recent partners uncovered $180K, $120K, and $42K in annual savings — then captured it in production, not just identified it on a slide.',
            fascinationBullet:
              'Why most "AI strategies" fail within 90 days (and the 3-question test that predicts success)',
            href: '/services/ai-audit-partnership',
          },
          {
            title: 'AI Implementation',
            problem:
              "You know what you want — a lead engine, voice AI, contract automation, a support bot — but your team doesn't have the specialized skills to ship it in production.",
            solution:
              'We build and deploy the system end-to-end. Proven playbooks: lead qualification chatbots, voice AI appointment setters, contract generation, marketing intelligence dashboards, SEO content engines, and render/doc automation.',
            outcome:
              'Live in 2-8 weeks with real numbers behind it: 200+ leads/month at $0.50 CPL, contracts cut from 2 days to 15 minutes, +108% consultations booked via voice AI.',
            fascinationBullet:
              'The 3 deployment mistakes that cause 80% of AI projects to fail in production (and how we prevent all three)',
            href: '/services/ai-implementation',
          },
          {
            title: 'VibeCode Interfaces',
            problem:
              'You need internal tools and customer-facing applications, but development timelines are measured in quarters, not weeks.',
            solution:
              'AI-powered rapid application development. We build functional, production-ready applications 5-10x faster than traditional development.',
            outcome:
              'PropertyBase multi-tenant SaaS in 21 days. Investor portal in 14 days. Lead engine in 12 days. All quoted at 4-6 months by traditional agencies.',
            fascinationBullet:
              'How we shipped a complete customer portal in 11 days that was quoted at 4 months by a traditional agency',
            href: '/services/vibecode',
          },
        ]}
      />

      {/* Testimonials Section */}
      <TestimonialsSection
        headline="What Our Clients Say"
        testimonials={[
          {
            quote:
              "We'd been 'exploring AI' for 18 months. These guys came in and had our first automation deployed in 19 days. Actual production deployment. I didn't think that was possible.",
            author: 'James Chen',
            role: 'CTO',
            company: 'B2B SaaS Company, 230 employees',
          },
          {
            quote:
              "The AI Profit Audit paid for our entire first year of services. They found $340K in annual savings we were completely blind to. And then they actually captured it.",
            author: 'Maria Santos',
            role: 'COO',
            company: 'Healthcare Technology, 180 employees',
          },
          {
            quote:
              "Our competitors are still doing demos. We're live. That head start is worth more than anything else they built for us — and they built a lot.",
            author: 'David Park',
            role: 'CEO',
            company: 'Financial Services, 320 employees',
          },
        ]}
      />

      {/* Urgency Section */}
      <UrgencySection
        headline="Two Paths. One Decision."
        pathOne={{
          title: 'Path One: Act Now',
          description:
            "You book a call this week. In 45 minutes, we identify your highest-impact AI opportunities. Within 6 weeks, you have production AI deployed. Within 90 days, you're seeing measurable ROI. Your team is freed up. Your costs are down. Your competitors are wondering how you moved so fast.",
        }}
        pathTwo={{
          title: 'Path Two: Wait',
          description:
            "You wait. Maybe revisit this in Q3. By then, your competitors have deployed. The talent market is tighter. The technology has shifted. And you're starting the same journey — except now it costs 2-3x more, and you're playing catch-up instead of leading.",
        }}
        statement="The companies that move now will own their markets for the next decade. The ones that wait will spend years trying to close the gap."
        capacityConstraint="We onboard 4 new enterprise clients per month. That's not artificial scarcity — it's the reality of doing high-touch implementation work."
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
      />

      {/* Guarantee Section */}
      <GuaranteeSection
        headline="The 'You'd Be Stupid to Say No' Offer"
        guarantee="Book a free AI Profit Audit. In 45 minutes, we'll identify at least 3 workflows where AI can save you $50K+ per year."
        details={[
          "If we can't find $150K+ in annual savings potential, we'll send you a detailed report explaining why — and you'll still walk away smarter than 95% of your competitors.",
          'No cost. No obligation. No catch.',
          'The only thing you risk is 45 minutes.',
        ]}
        riskStatement="45 minutes of your time"
        upsideStatement="Potentially hundreds of thousands in annual savings"
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
      />
    </main>
  );
};

export default HomePage;
